import { useEffect, useMemo, useRef, useState } from 'react'
import { useGLTF } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { AnimationClip, AnimationMixer, Group, LoopRepeat, Material, Mesh } from 'three'
import { tunnelShaderStele } from './TunnelShaderStele'
import { tunnelShader } from './TunnelShader'
import type { TrackController } from '../track/trackController'
import { useTrackController } from '../track/useTrackController'
import {
    getTransitionRanges,
    useTunnelTransitionVisibility,
} from './tunnelTransitionHelpers'
import { gltfAsset } from '../../assetStore'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'

type BoundaryObjectEntry = {
    mixer: AnimationMixer | null
    object: Group
}

export function TunnelBoundaries() {
    const { scene: startScene, animations: startAnimations } = useGLTF(gltfAsset.Tunnel_Start)
    const { scene: exitScene, animations: exitAnimations } = useGLTF(gltfAsset.Tunnel_Exit)
    const trackController = useTrackController()
    const isStele = useSteleBreakpoint()
    const tunnelMaterial = isStele ? tunnelShaderStele : tunnelShader
    const [startGroup, setStartGroup] = useState<Group | null>(null)
    const [endGroup, setEndGroup] = useState<Group | null>(null)
    const mixersRef = useRef<AnimationMixer[]>([])
    const { startObjects, endObjects } = useMemo(
        () => createTunnelBoundaryObjects(
            startScene,
            startAnimations,
            exitScene,
            exitAnimations,
            trackController,
            tunnelMaterial,
        ),
        [startScene, startAnimations, exitScene, exitAnimations, trackController, tunnelMaterial],
    )

    useEffect(() => {
        mixersRef.current = [
            ...startObjects.map(entry => entry.mixer).filter((mixer): mixer is AnimationMixer => mixer != null),
            ...endObjects.map(entry => entry.mixer).filter((mixer): mixer is AnimationMixer => mixer != null),
        ]

        if (!startGroup || !endGroup) {
            return
        }

        startGroup.clear()
        startObjects.forEach((entry) => {
            startGroup.add(entry.object)
        })

        endGroup.clear()
        endObjects.forEach((entry) => {
            endGroup.add(entry.object)
        })

        return () => {
            startGroup.clear()
            endGroup.clear()
            mixersRef.current = []
        }
    }, [startGroup, endGroup, startObjects, endObjects])

    useFrame((_, deltaSeconds) => {
        mixersRef.current.forEach((mixer) => {
            mixer.update(deltaSeconds)
        })
    })

    useTunnelTransitionVisibility(
        startGroup,
        (transition, part) => {
            if (part === 'entrance') {
                return [`TransitionTunnelStart_transition${transition}`]
            }

            if (part === 'exit') {
                return [`TransitionTunnelExit_transition${transition}`]
            }

            return []
        },
    )

    useTunnelTransitionVisibility(
        endGroup,
        (transition, part) => {
            if (part === 'entrance') {
                return [`TransitionTunnelStart_transition${transition}`]
            }

            if (part === 'exit') {
                return [`TransitionTunnelExit_transition${transition}`]
            }

            return []
        },
    )

    return (
        <group>
            <group ref={setStartGroup} />
            <group ref={setEndGroup} />
        </group>
    )
}

function createTunnelBoundaryObjects(
    startScene: THREE.Group,
    startAnimations: AnimationClip[],
    exitScene: THREE.Group,
    exitAnimations: AnimationClip[],
    trackController: TrackController,
    tunnelMaterial: Material,
) {
    const transitions = getTransitionRanges(trackController)
    const startObjects: BoundaryObjectEntry[] = []
    const endObjects: BoundaryObjectEntry[] = []

    for (const transition of transitions) {
        startObjects.push(
            createTransitionBoundaryObject(
                startScene,
                startAnimations,
                'Tunnel_Start',
                `TransitionTunnelStart_${transition.key}`,
                transition.start,
                trackController,
                tunnelMaterial,
            ),
        )
        endObjects.push(
            createTransitionBoundaryObject(
                exitScene,
                exitAnimations,
                'Tunnel_Exit',
                `TransitionTunnelExit_${transition.key}`,
                transition.end,
                trackController,
                tunnelMaterial,
            ),
        )
    }

    return { startObjects, endObjects }
}

function createTransitionBoundaryObject(
    sourceAsset: THREE.Group,
    animations: AnimationClip[],
    animationName: string,
    name: string,
    trackPosition: number,
    trackController: TrackController,
    tunnelMaterial: Material,
): BoundaryObjectEntry {
    const model = sourceAsset.clone(true)
    applyTunnelShaderToModel(model, tunnelMaterial)

    const mixer = playBoundaryAnimation(model, animations, animationName)
    const frame = trackController.frameAtPosition(trackPosition)

    const container = new THREE.Group()
    container.name = name
    container.position.copy(frame.position)
    container.quaternion.copy(frame.trackQuaternion)
    container.add(model)

    return {
        mixer,
        object: container,
    }
}

function applyTunnelShaderToModel(root: THREE.Object3D, tunnelMaterial: Material) {
    root.traverse((child) => {
        if (!(child instanceof Mesh)) {
            return
        }

        child.castShadow = false
        child.receiveShadow = false

        if (Array.isArray(child.material)) {
            child.material = child.material.map((material) =>
                shouldUseTunnelShader(child.name, material.name) ? tunnelMaterial : material,
            )
            return
        }

        if (shouldUseTunnelShader(child.name, child.material.name)) {
            child.material = tunnelMaterial
        }
    })
}

function shouldUseTunnelShader(meshName: string, materialName: string) {
    const meshLabel = meshName.toLowerCase()
    const materialLabel = materialName.toLowerCase()

    if (materialLabel === 'lvl4_a') {
        return false
    }

    return (
        meshLabel.includes('tunnel') ||
        meshLabel.includes('inside') ||
        materialLabel.includes('tunnel') ||
        materialLabel.includes('inside')
    )
}

function playBoundaryAnimation(
    root: THREE.Object3D,
    animations: AnimationClip[],
    animationName: string,
): AnimationMixer | null {
    const clip = animations.find(entry => entry.name === animationName)
    if (!clip) {
        return null
    }

    const mixer = new AnimationMixer(root)
    const action = mixer.clipAction(clip)
    action.enabled = true
    action.clampWhenFinished = false
    action.setLoop(LoopRepeat, Infinity)
    action.play()
    return mixer
}
